import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import React from 'react';
import ComplianceOperationTag from '../../../../../../../../../../../../../Common/ComplianceOperationTag/ComplianceOperationTag';
import AdvancedAnalysisItemTag from '../../../../../../../../../../../../../Components/AdvancedAnalysisItemTag/AdvancedAnalysisItemTag';
import {
  AdvancedAnalysisItemType, AssignmentType, ComplianceOperationItemFieldsFragment, ComplianceOperationItemScoreRuleOperator,
} from '../../../../../../../../../../../../../generated/core-api/graphql-react-query';
import useRatioFormat from '../../../../../../../../../../../../../Hooks/useRatioFormat';
import TopicTag from '../../../../../../../../CommandPage/Components/CommandItem/Components/TopicTag/TopicTag';
import StyledBox from './styles';
import WeightCell from './WeightCell';

interface ComplianceOperationLogicProps {
  advancedAnalysisMap: Map<string, { name: string; type?: AdvancedAnalysisItemType | null }>;
  childrenValue: unknown;
  collapsible?: boolean;
  editable?: boolean;
  mode?: 'score' | 'weight';
  operator: ComplianceOperationItemScoreRuleOperator;
  operationMap?: Map<string, Pick<ComplianceOperationItemFieldsFragment, 'operator' | 'title'>>;
  title?: string;
  topicMap: Map<string, { text: string; color?: string | null }>;
}

interface BaseOperationChild {
  assignmentId: string;
  id: string;
  type: AssignmentType;
}

interface TopicChild extends BaseOperationChild {
  type: AssignmentType.TopicComplianceOperationItemAssignment;
  weight: number;
}

interface AdvancedAnalysisChild extends BaseOperationChild {
  type: AssignmentType.AdvancedAnalysisItemComplianceOperationItemAssignment;
  weight: number;
}

interface OperationChild extends BaseOperationChild {
  children: ParsedOperationChild[];
  operation: {
    id: string;
    operator: ComplianceOperationItemScoreRuleOperator;
    title: string;
  };
  type: AssignmentType.ComplianceOperationItemComplianceOperationItemAssignment;
  weight: number;
}

type ParsedOperationChild = TopicChild | AdvancedAnalysisChild | OperationChild;

interface ResultBaseChild {
  id: string;
  score: number | null;
  weight: number;
}

interface ResultTopicChild extends ResultBaseChild {
  type: 'topic';
}

interface ResultAdvancedAnalysisChild extends ResultBaseChild {
  type: 'advancedAnalysis';
}

interface ResultOperationChild extends ResultBaseChild {
  children: ResultParsedOperationChild[];
  operator: ComplianceOperationItemScoreRuleOperator;
  type: 'operation';
}

type ResultParsedOperationChild = ResultTopicChild | ResultAdvancedAnalysisChild | ResultOperationChild;

function parseChildren<T>(childrenValue: unknown): T[] {
  if (!childrenValue) {
    return [];
  }

  let parsedValue: unknown;
  try {
    parsedValue = typeof childrenValue === 'string' ? JSON.parse(childrenValue) : childrenValue;
  } catch {
    return [];
  }

  if (!Array.isArray(parsedValue)) {
    return [];
  }

  return parsedValue as T[];
}

function getChildName(
  child: ParsedOperationChild | ResultParsedOperationChild,
  topicMap: Map<string, { text: string; color?: string | null }>,
  advancedAnalysisMap: Map<string, { name: string; type?: AdvancedAnalysisItemType | null }>,
  operationMap: Map<string, Pick<ComplianceOperationItemFieldsFragment, 'operator' | 'title'>>,
): string {
  if (child.type === AssignmentType.ComplianceOperationItemComplianceOperationItemAssignment) {
    return child.operation.title.toLowerCase();
  }
  if (child.type === 'operation') {
    return (operationMap.get(child.id)?.title ?? child.id).toLowerCase();
  }
  if (child.type === AssignmentType.TopicComplianceOperationItemAssignment || child.type === 'topic') {
    return (topicMap.get(child.id)?.text ?? child.id).toLowerCase();
  }
  return (advancedAnalysisMap.get(child.id)?.name ?? child.id).toLowerCase();
}

function LogicLine(props: {
  advancedAnalysisMap: Map<string, { name: string; type?: AdvancedAnalysisItemType | null }>;
  child: ParsedOperationChild;
  editable: boolean;
  topicMap: Map<string, { text: string; color?: string | null }>;
}) {
  const {
    advancedAnalysisMap, child, editable, topicMap,
  } = props;

  if (child.type === AssignmentType.ComplianceOperationItemComplianceOperationItemAssignment) {
    return (
      <div className="nested">
        <div className="nested-panel">
          <div className="line compliance-operation">
            <ComplianceOperationTag displayText={child.operation.title} />
            <WeightCell value={child.weight} />
          </div>
          <ComplianceOperationLogic
            operator={child.operation.operator}
            childrenValue={child.children}
            topicMap={topicMap}
            advancedAnalysisMap={advancedAnalysisMap}
            editable={editable}
          />
        </div>
      </div>
    );
  }

  if (child.type === AssignmentType.TopicComplianceOperationItemAssignment) {
    const topic = topicMap.get(child.id);
    return (
      <div className="line topic">
        <TopicTag topic={topic?.text ?? child.id} color={topic?.color} isCompliance />
        <WeightCell value={child.weight} />
      </div>
    );
  }

  const advancedAnalysisItem = advancedAnalysisMap.get(child.id);
  return (
    <div className="line advanced-analysis">
      <AdvancedAnalysisItemTag
        displayText={advancedAnalysisItem?.name ?? child.id}
        type={advancedAnalysisItem?.type ?? null}
      />
      <WeightCell value={child.weight} />
    </div>
  );
}

function ScoreCell(props: { score: number | null; weight: number }) {
  const { score, weight } = props;
  const ratioFormat = useRatioFormat('percentAndRatio');

  return (
    <Typography className="score">
      {ratioFormat.format(score === null ? { numerator: 0, denominator: 0 } : { numerator: score * weight, denominator: weight })}
    </Typography>
  );
}

function ResultLogicLine(props: {
  advancedAnalysisMap: Map<string, { name: string; type?: AdvancedAnalysisItemType | null }>;
  child: ResultParsedOperationChild;
  operationMap: Map<string, Pick<ComplianceOperationItemFieldsFragment, 'operator' | 'title'>>;
  topicMap: Map<string, { text: string; color?: string | null }>;
}) {
  const {
    advancedAnalysisMap, child, operationMap, topicMap,
  } = props;

  if (child.type === 'operation') {
    const operation = operationMap.get(child.id);
    return (
      <div className="nested">
        <div className="nested-panel">
          <div className="line compliance-operation">
            <ComplianceOperationTag displayText={operation?.title ?? child.id} />
            <ScoreCell score={child.score} weight={child.weight} />
          </div>
          <ComplianceOperationLogic
            operator={operation?.operator ?? child.operator}
            childrenValue={child.children}
            topicMap={topicMap}
            advancedAnalysisMap={advancedAnalysisMap}
            operationMap={operationMap}
            editable={false}
            mode="score"
          />
        </div>
      </div>
    );
  }

  if (child.type === 'topic') {
    const topic = topicMap.get(child.id);
    return (
      <div className="line topic">
        <TopicTag topic={topic?.text ?? child.id} color={topic?.color} isCompliance />
        <ScoreCell score={child.score} weight={child.weight} />
      </div>
    );
  }

  const advancedAnalysisItem = advancedAnalysisMap.get(child.id);
  return (
    <div className="line advanced-analysis">
      <AdvancedAnalysisItemTag
        displayText={advancedAnalysisItem?.name ?? child.id}
        type={advancedAnalysisItem?.type ?? null}
      />
      <ScoreCell score={child.score} weight={child.weight} />
    </div>
  );
}

export default function ComplianceOperationLogic(props: ComplianceOperationLogicProps) {
  const {
    advancedAnalysisMap,
    childrenValue,
    collapsible = false,
    editable = true,
    mode = 'weight',
    operator,
    operationMap = new Map(),
    title,
    topicMap,
  } = props;
  const [collapsed, setCollapsed] = React.useState(true);

  const parsedChildren = React.useMemo(
    () => parseChildren<ParsedOperationChild | ResultParsedOperationChild>(childrenValue),
    [childrenValue],
  );

  const sortedChildren = React.useMemo(() => (
    [...parsedChildren].sort((a, b) => (
      getChildName(a, topicMap, advancedAnalysisMap, operationMap).localeCompare(
        getChildName(b, topicMap, advancedAnalysisMap, operationMap),
      )
    ))
  ), [parsedChildren, topicMap, advancedAnalysisMap, operationMap]);

  if (sortedChildren.length === 0) {
    return null;
  }

  return (
    <StyledBox>
      {title && (
        <Typography className="title">
          {title}
        </Typography>
      )}
      {collapsible && (
        <Box display="flex" alignItems="center">
          <IconButton size="small" onClick={() => { setCollapsed(!collapsed); }}>
            {collapsed ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
          </IconButton>
        </Box>
      )}
      {(!collapsible || !collapsed) && (
        <div className="logic-body">
          <div className={`operator ${operator}`}>
            {operator.toUpperCase()}
          </div>
          <div className="content">
            {sortedChildren.map((child) => (
              mode === 'score' ? (
                <ResultLogicLine
                  key={`${child.type}-${child.id}`}
                  child={child as ResultParsedOperationChild}
                  topicMap={topicMap}
                  advancedAnalysisMap={advancedAnalysisMap}
                  operationMap={operationMap}
                />
              ) : (
                <LogicLine
                  key={(child as ParsedOperationChild).assignmentId}
                  child={child as ParsedOperationChild}
                  topicMap={topicMap}
                  advancedAnalysisMap={advancedAnalysisMap}
                  editable={editable}
                />
              )
            ))}
          </div>
        </div>
      )}
    </StyledBox>
  );
}
